import { isValidCents } from './cents.js'
import type { Cents } from './cents.js'

/**
 * **Texto de formulário → centavos inteiros, sem passar por float.**
 *
 * `Docs/03-Arquitetura/Dinheiro, datas e fuso.md` diz "nenhum float, em nenhum
 * lugar, nunca", e `format.ts` é a saída — a borda de renderização. Este
 * arquivo é a **entrada**, que faltava: um `<input>` devolve `string`, e
 * `parseFloat(x) * 100` é como o float volta pela porta dos fundos.
 *
 * ```
 * parseFloat('8.20')  * 100  →  819.9999999999999
 * parseFloat('0.29')  * 100  →   28.999999999999996
 * ```
 *
 * **Os números acima são medidos, e o exemplo que costuma acompanhar essa
 * frase não é.** `parseFloat('312.45') * 100` dá `31245` **exato** — foi o
 * primeiro valor que testei, e ele passa. É por isso que o bug sobrevive a uma
 * conferência rápida: medido neste Node, 18.351 dos 200.000 primeiros valores
 * de duas casas produzem não-inteiro, ou seja **um em onze**, e os dez que
 * fecham são os que alguém experimenta.
 *
 * O que acontece com o não-inteiro depende de quem o recebe, e a assimetria é
 * o ponto: `Math.round` **recupera** — medido, zero erros em todos os valores
 * de duas casas de R$ 0 a R$ 30.000 —, mas `Math.trunc`, `parseInt`, `| 0` e
 * "guardar como está" não. `Math.trunc(parseFloat('8.20') * 100)` é `819`: um
 * centavo a menos, para baixo, calado. E guardado como está, o valor deixa de
 * ser inteiro e `isValidCents` passa a reprovar dado que já está no banco.
 *
 * Aqui não há divisão nem multiplicação nenhuma: as casas decimais são
 * completadas com `padEnd`, os dígitos são **concatenados**, e o único `Number`
 * roda sobre uma string de no máximo 15 dígitos — abaixo de 2^53, onde a
 * conversão é exata.
 *
 * O caminho por float **com `Math.round`** seria equivalente, e há prova disso
 * no docblock de `MAX_DIGITS`: o teto de 15 dígitos limita o erro a menos de
 * meio centavo. A prova é a razão de o teto não poder subir, e não uma licença
 * para usar float — a equivalência depende do arredondamento estar lá, e nada
 * no código declara isso.
 *
 * ## O que ela aceita
 *
 * O que se digita no Brasil, e o que se cola de volta da própria tela:
 * `1.234,56`, `1234,56`, `R$ 1.234,56` (com o espaço **não separável** que o
 * `Intl` do pt-BR escreve), `-R$ 1.000,00`, `1234.56`, `,50`, `50,`.
 *
 * ## O que ela recusa, e por que recusar é a resposta certa
 *
 * - **mais de duas casas decimais.** Centavo é a menor unidade; arredondar
 *   `312,456` inventaria meio centavo que ninguém digitou, num campo de saldo
 *   onde a diferença some calada.
 * - **grupo de milhar que não tem três dígitos** (`1.2345,00`).
 * - **`0.500`**, que é o único caso de verdade ambíguo: milhar (R$ 500) ou
 *   decimal (R$ 0,50), com mil vezes de diferença e nenhuma leitura claramente
 *   mais provável. `1.234` **não** é ambíguo — a leitura decimal teria três
 *   casas, que já é recusa —, então sobra uma só e ela é aceita.
 * - **valor que não caberia em centavos exatos.** `isSafeInteger` é a mesma
 *   fronteira que `cents.ts` já cobra: acima de 2^53 a soma deixa de ser
 *   exata, e soma inexata é o bug que centavos inteiros existem para impedir.
 *
 * ## Campo em branco é um estado, não zero
 *
 * `balanceCents: null` e `balanceCents: 0` dizem coisas diferentes — conta sem
 * saldo conhecido versus conta zerada —, e os dois campos que esta função
 * alimenta (`balanceCents`, `creditLimitCents`) são anuláveis no domínio. Um
 * `0` de fachada para campo vazio escreveria "zerado" onde a pessoa não
 * respondeu. Daí `'empty'` ser um status e não um valor.
 */

/** Por que o texto não vira dinheiro. Código, não frase: a tela escolhe como
 * dizer, e o teste compara sem depender da redação. */
export type AmountFailure =
  | 'sem-digito'
  | 'caractere-invalido'
  | 'centavos-demais'
  | 'agrupamento-invalido'
  | 'grande-demais'

export type ParsedAmount =
  /** Só espaço, ou só `R$`. O campo não foi respondido. */
  | { readonly status: 'empty' }
  | { readonly status: 'ok'; readonly cents: Cents }
  | {
      readonly status: 'invalid'
      readonly reason: AmountFailure
      /** Em português, para ir direto à tela. */
      readonly message: string
    }

const MESSAGES: Record<AmountFailure, string> = {
  'sem-digito': 'Digite um número — só o sinal ou a vírgula não é um valor.',
  'caractere-invalido': 'Use só dígitos, ponto e vírgula. "R$" e espaços são ignorados.',
  'centavos-demais': 'O centavo é a menor unidade: no máximo duas casas depois da vírgula.',
  'agrupamento-invalido': 'Separador de milhar vem em grupo de três dígitos: 1.234,56.',
  'grande-demais': 'Valor grande demais para ser somado com exatidão em centavos.',
}

function invalid(reason: AmountFailure): ParsedAmount {
  return { status: 'invalid', reason, message: MESSAGES[reason] }
}

/**
 * O teto de dígitos, e ele é **mais apertado que `isSafeInteger` de
 * propósito.** Com 15 dígitos toda resposta cabe: o maior valor possível é
 * `999999999999999`, abaixo de 2^53. Com 16 a resposta passa a **depender do
 * valor** — `9007199254740991` é seguro e `9007199254740992` não é —, e um
 * limite que depende do valor não é um limite: é um lugar onde o comportamento
 * muda no meio da faixa, sem nada na tela dizendo onde.
 *
 * 15 dígitos de centavos são R$ 9.999.999.999.999,99. O princípio 8 diz que
 * este app é para duas pessoas.
 *
 * **É este teto que sustenta a guarda de `isValidCents` lá embaixo ser
 * inalcançável** — e a guarda continua escrita porque o dia em que este número
 * mudar, é ela que segura.
 *
 * ## E é ele que torna `Math.round(Number(s) * 100)` equivalente — o que é a
 * razão de o teto não poder subir
 *
 * A varredura de mutação deixou vivo o mutante que troca a concatenação de
 * dígitos por `Math.round(Number(s) * 100)`, e a primeira redação disto dizia
 * "equivalente medido em 600 mil amostras". **Não é medição, é teorema**, e o
 * teto é a hipótese dele:
 *
 * - `100·x` é **inteiro** (o texto aceito tem no máximo duas casas) e, pelo
 *   teto, `100·x < 10^15`;
 * - `Number(s)` arredonda uma vez: devolve `x·(1 + δ₁)`, com `|δ₁| ≤ 2^-53`;
 * - a multiplicação por 100 arredonda outra: `100·x·(1 + δ₁)·(1 + δ₂)`, com
 *   `|δ₂| ≤ 2^-53`;
 * - o erro absoluto fica abaixo de `100·x · 2^-52 < 10^15 · 2^-52 ≈ 0,222`.
 *
 * `0,222 < 0,5`, e o valor exato é inteiro: `Math.round` recupera **sempre**.
 * Nenhum valor do domínio aceito cai exatamente entre dois inteiros — e com um
 * teto de 16 dígitos a mesma conta dá `≈ 2,22`, que é maior que 0,5 e derruba a
 * garantia.
 *
 * O código continua sem float mesmo assim, e agora com o motivo exato: a
 * equivalência **depende de o `Math.round` estar lá**, e nada no código declara
 * essa dependência. `Math.trunc` no lugar dele perde um centavo (mutante que
 * morre), e o teto é uma constante que alguém sobe sem pensar em ponto
 * flutuante. A aritmética inteira não tem hipótese nenhuma.
 */
const MAX_DIGITS = 15

/** Espaço comum, não separável (o que o `Intl` do pt-BR escreve) e o estreito. */
const BLANKS = /[\s\u00A0\u202F]/g
const CURRENCY = /^R\$/i

/** Grupo de milhar: exatamente três dígitos. */
function isGroup(part: string): boolean {
  return /^\d{3}$/.test(part)
}

/**
 * O primeiro grupo de um número agrupado tem de 1 a 3 dígitos **e não pode ter
 * zero à esquerda**. É essa segunda metade que faz `0.500` ser recusado em vez
 * de virar R$ 500: ninguém escreve quinhentos reais assim, e a outra leitura
 * (cinquenta centavos) é mil vezes menor.
 */
function isLeadingGroup(part: string): boolean {
  return /^[1-9]\d{0,2}$/.test(part)
}

/**
 * A vírgula é sempre decimal. O ponto depende: com exatamente três dígitos
 * depois e um primeiro grupo válido antes, ele é milhar (`1.234`); nos demais
 * casos é decimal (`1234.56`, `1.5`).
 */
function pointIsGrouping(before: string, after: string): boolean {
  return isGroup(after) && isLeadingGroup(before)
}

/**
 * O último separador é o decimal?
 *
 * Três respostas, nesta ordem:
 *
 * 1. **ele se repete** (`1.234.567`, `1,23,45`) → não. Um número não tem duas
 *    vírgulas decimais, então o caractere repetido só pode ser o de milhar; se
 *    os grupos não fecharem, quem recusa é a validação de agrupamento.
 * 2. **é vírgula** → sim, sempre. No pt-BR a vírgula nunca separa milhar.
 * 3. **é ponto** → depende do que vem depois: três dígitos com um primeiro
 *    grupo válido antes é milhar (`1.234`); qualquer outra coisa é decimal
 *    (`1234.56`, `1.5`).
 */
function lastSeparatorIsDecimal(parts: string[], separators: string[]): boolean {
  const last = separators[separators.length - 1]
  if (separators.slice(0, -1).some((separator) => separator === last)) return false
  if (last === ',') return true
  return !pointIsGrouping(parts.slice(0, -1).join(''), parts.at(-1) ?? '')
}

/** Junta as partes inteiras e a fração em um número de centavos exato. */
function toCents(integerParts: string[], fraction: string, negative: boolean): ParsedAmount {
  if (fraction.length > 2) return invalid('centavos-demais')

  const digits = (integerParts.join('') + fraction.padEnd(2, '0')).replace(/^0+/, '')
  if (digits.length > MAX_DIGITS) return invalid('grande-demais')

  const magnitude = digits === '' ? 0 : Number(digits)
  // O zero não tem sinal: sem esta linha, um campo com `-0,00` produziria `-0`,
  // e `formatBRL` teria de desfazer isso de novo do outro lado.
  const cents = magnitude === 0 ? 0 : negative ? -magnitude : magnitude
  if (!isValidCents(cents)) return invalid('grande-demais')
  return { status: 'ok', cents }
}

export function parseAmountInput(raw: string): ParsedAmount {
  let body = raw.replace(BLANKS, '').replace(CURRENCY, '')

  // O sinal pode vir dos dois lados do símbolo: o teclado escreve `-R$ 10` e o
  // `Intl` do pt-BR também, mas `R$ -10` é o que sai de quem digita na ordem em
  // que fala. As duas entram.
  const sign = /^[+-]/.exec(body)
  const negative = sign?.[0] === '-'
  if (sign !== null) body = body.slice(1).replace(CURRENCY, '')

  if (body === '') return sign === null ? { status: 'empty' } : invalid('sem-digito')
  if (/[^\d.,]/.test(body)) return invalid('caractere-invalido')
  if (!/\d/.test(body)) return invalid('sem-digito')

  // `,50` é meio real e `50,` é cinquenta reais: as duas formas saem de teclado
  // de celular o tempo todo, e nenhuma delas é ambígua.
  if (/^[.,]/.test(body)) body = `0${body}`
  if (/[.,]$/.test(body)) body = body.slice(0, -1)

  if (!/^\d+(?:[.,]\d+)*$/.test(body)) return invalid('agrupamento-invalido')

  const parts = body.split(/[.,]/)
  const separators = body.match(/[.,]/g) ?? []

  if (separators.length === 0) return toCents(parts, '', negative)

  const decimalIsLast = lastSeparatorIsDecimal(parts, separators)
  const integerParts = decimalIsLast ? parts.slice(0, -1) : parts
  const fraction = decimalIsLast ? (parts.at(-1) ?? '') : ''

  // O agrupamento é cobrado só quando há mais de uma parte inteira: `12345,67`
  // não tem grupo nenhum a validar, e exigir três dígitos ali recusaria um
  // número perfeitamente comum.
  if (integerParts.length > 1) {
    const [first, ...rest] = integerParts
    if (first === undefined || !isLeadingGroup(first)) return invalid('agrupamento-invalido')
    if (!rest.every(isGroup)) return invalid('agrupamento-invalido')
  }

  return toCents(integerParts, fraction, negative)
}

/**
 * Centavos → o texto do campo de edição: `31245` vira `'312,45'`.
 *
 * **Sem separador de milhar e sem `R$`**, ao contrário de `formatBRL`: isto vai
 * para dentro de um `<input>`, onde o que importa é o valor voltar igual se
 * ninguém o tocar — e é `parseAmountInput` quem o lê de volta. `format.ts`
 * continua sendo a borda de leitura; esta é a de edição.
 *
 * A separação em reais e centavos é feita em **string**, não com `/ 100`: a
 * divisão é a mesma porta dos fundos que `parseAmountInput` existe para fechar,
 * e aqui ela não é necessária nem uma vez.
 */
export function centsToInput(value: Cents): string {
  if (!isValidCents(value)) {
    throw new Error(`centsToInput: valor precisa ser inteiro em centavos, recebi ${String(value)}`)
  }
  const digits = String(Math.abs(value)).padStart(3, '0')
  const sign = value < 0 ? '-' : ''
  return `${sign}${digits.slice(0, -2)},${digits.slice(-2)}`
}
